import { Body, H4, Stack, useWindowProperties } from '@qred/components-library';
import React, { ReactNode } from 'react';
import ValidationErrorMessage from '~/components/shared/ValidationErrorMessage/ValidationErrorMessage';
import { NestedObjects } from '~/declarations/nested-objects.d';
import useTranslate from '~/hooks/useTranslate';
import { TranslationsKeys } from '~/languages/translations.d';
import { PasscodeInput } from './PasscodeInput';
import { usePasscodeFlow } from './usePasscodeFlow';

type TranslationKey = NestedObjects<TranslationsKeys>;

interface ViewLayoutProps {
  title: TranslationKey;
  description: TranslationKey;
  isCentered?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
}

const ViewLayout = ({
  icon,
  title,
  description,
  isCentered = false,
  children,
}: ViewLayoutProps) => {
  const translate = useTranslate();

  return (
    <Stack
      align={isCentered ? 'center' : undefined}
      spacing={'xxl'}
      data-testid="create-passcode"
    >
      <H4 data-testid="passcode-title" data-record>
        {translate(title)}
      </H4>
      {icon}
      <Body
        size="lg"
        color="Text/Default"
        align={isCentered ? 'center' : undefined}
        data-record
      >
        {translate(description)}
      </Body>
      {children}
    </Stack>
  );
};

const StatusIcon = ({
  src,
  isDesktop,
}: {
  src: string;
  isDesktop: boolean;
}) => (
  <img
    src={src}
    alt=""
    style={{
      alignSelf: 'center',
      width: isDesktop ? '240px' : '160px',
      height: isDesktop ? '240px' : '160px',
    }}
  />
);

interface PasscodeCardContentProps {
  /** Differs per host: continue to signing, or return to the app. */
  successDescription: TranslationKey;
  /** Whether the flow should default to the already-set view. */
  hasExistingPasscode: boolean;
}

/**
 * The matching buttons render separately, in the offer step's footer or
 * alongside this on the standalone page. `usePasscodeFlow` keeps the two
 * in sync.
 */
export const PasscodeCardContent = ({
  successDescription,
  hasExistingPasscode,
}: PasscodeCardContentProps) => {
  const translate = useTranslate();
  const { isDesktop } = useWindowProperties();
  const { view, setCreatePasscode, setVerifyConfirmation } = usePasscodeFlow(
    hasExistingPasscode
  );

  // formatMessage returns a plain string for a key without rich-text values;
  // the hook's ReactNode return type is wider than this case.
  const inputLabel = translate('CreatePasscode.InputLabel') as string;

  switch (view.kind) {
    case 'alreadySet':
      return (
        <ViewLayout
          title="CreatePasscode.AlreadySetTitle"
          description="CreatePasscode.AlreadySetDescription"
        />
      );

    case 'create':
      return (
        <ViewLayout
          title="CreatePasscode.CreateTitle"
          description="CreatePasscode.CreateDescription"
        >
          <PasscodeInput
            key="create"
            value={view.passcode}
            onChange={setCreatePasscode}
            hasError={false}
            ariaLabel={inputLabel}
          />
        </ViewLayout>
      );

    case 'verify':
      return (
        <ViewLayout
          title="CreatePasscode.VerifyTitle"
          description="CreatePasscode.VerifyDescription"
        >
          <PasscodeInput
            key="verify"
            value={view.confirmation}
            onChange={setVerifyConfirmation}
            hasError={view.hasMismatch}
            ariaLabel={inputLabel}
          />
          {view.hasMismatch && (
            <ValidationErrorMessage>
              {translate('CreatePasscode.MismatchError')}
            </ValidationErrorMessage>
          )}
        </ViewLayout>
      );

    case 'submitError':
      return (
        <ViewLayout
          isCentered
          icon={
            <StatusIcon
              src="/illustrations/general/error.png"
              isDesktop={isDesktop}
            />
          }
          title="CreatePasscode.SubmitErrorTitle"
          description="CreatePasscode.SubmitError"
        />
      );

    case 'success':
      return (
        <ViewLayout
          isCentered
          icon={<StatusIcon src="/success_check.png" isDesktop={isDesktop} />}
          title="CreatePasscode.SuccessTitle"
          description={successDescription}
        />
      );

    default: {
      const exhaustiveCheck: never = view;
      return exhaustiveCheck;
    }
  }
};
